export function generateSettingsPage(pascal: string, kebab: string): string {
  return `import { getFormSettings } from '@admin/actions/forms'
import { ${pascal}SettingsPageContent } from './settings-page-content'

export default async function ${pascal}SettingsPage() {
  const settings = await getFormSettings('${kebab}')

  return <${pascal}SettingsPageContent initialSettings={settings} />
}
`
}

export function generateSettingsPageContent(pascal: string, kebab: string, label: string): string {
  return `'use client'

import {
  testFormWebhook,
  type FormSettingsData,
  upsertFormSettings,
} from '@admin/actions/forms'
import { PageHeader } from '@admin/components/shared/page-header'
import { Button } from '@admin/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@admin/components/ui/form'
import { Input } from '@admin/components/ui/input'
import { Switch } from '@admin/components/ui/switch'
import { Textarea } from '@admin/components/ui/textarea'
import { LoaderCircle } from 'lucide-react'
import * as React from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

interface ${pascal}SettingsPageContentProps {
  initialSettings: FormSettingsData | null
}

interface ${pascal}SettingsFormValues {
  notificationEmails: string
  webhookUrl: string
  webhookEnabled: boolean
}

export function ${pascal}SettingsPageContent({
  initialSettings,
}: ${pascal}SettingsPageContentProps) {
  const form = useForm<${pascal}SettingsFormValues>({
    defaultValues: {
      notificationEmails: initialSettings?.notificationEmails ?? '',
      webhookUrl: initialSettings?.webhookUrl ?? '',
      webhookEnabled: initialSettings?.webhookEnabled ?? false,
    },
  })
  const [isSaving, startSaveTransition] = React.useTransition()
  const [isTesting, startTestTransition] = React.useTransition()
  const webhookUrl = form.watch('webhookUrl')
  const webhookEnabled = form.watch('webhookEnabled')

  const handleSave = (values: ${pascal}SettingsFormValues) => {
    startSaveTransition(async () => {
      const result = await upsertFormSettings('${kebab}', {
        notificationEmails: values.notificationEmails || null,
        webhookUrl: values.webhookUrl || null,
        webhookEnabled: values.webhookEnabled,
      })
      if (result.success) {
        toast.success('Settings saved successfully')
      } else {
        toast.error(result.error || 'Failed to save settings')
      }
    })
  }

  const handleTestWebhook = () => {
    startTestTransition(async () => {
      const result = await testFormWebhook('${kebab}')
      if (result.success) {
        toast.success('Test webhook sent successfully')
      } else {
        toast.error(result.error || 'Failed to send test webhook')
      }
    })
  }

  return (
    <>
      <PageHeader title="${label} Settings" />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSave)}
          className="space-y-6 rounded-sm border p-6 mx-6 mt-6"
        >
          <FormField
            control={form.control}
            name="notificationEmails"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Notification Emails</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="email@example.com, another@example.com"
                    rows={3}
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  Comma-separated list of email addresses to notify on new submissions.
                </FormDescription>
              </FormItem>
            )}
          />

          <div className="space-y-4 border-t pt-6">
            <FormField
              control={form.control}
              name="webhookUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Webhook URL</FormLabel>
                  <FormControl>
                    <Input
                      type="url"
                      placeholder="https://hooks.example.com/endpoint"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Receive a POST request with form data on each submission.
                  </FormDescription>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="webhookEnabled"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormLabel>Enable Webhook</FormLabel>
                </FormItem>
              )}
            />

            {webhookUrl && webhookEnabled && (
              <Button
                type="button"
                variant="outline"
                onClick={handleTestWebhook}
                disabled={isTesting}
              >
                {isTesting ? (
                  <>
                    <LoaderCircle className="animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Test Webhook'
                )}
              </Button>
            )}
          </div>

          <div className="border-t pt-6">
            <Button type="submit" disabled={isSaving}>
              {isSaving ? (
                <>
                  <LoaderCircle className="animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Settings'
              )}
            </Button>
          </div>
        </form>
      </Form>
    </>
  )
}
`
}
